import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Icon from '../AppIcon';

const menuGroups = [
  {
    groupLabel: 'OVERVIEW',
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    ],
  },
  {
    groupLabel: 'OPERATIONS',
    items: [
      {
        id: 'transactions', label: 'Transactions', icon: 'ArrowLeftRight',
        children: [
          { id: 'deposits', label: 'Deposits', path: '/transactions/deposits', icon: 'ArrowDownCircle' },
          { id: 'payouts', label: 'Payouts', path: '/transactions/payouts', icon: 'ArrowUpCircle' },
          { id: 'refunds', label: 'Refunds', path: '/payment-operations/refunds', icon: 'RotateCcw' },
          { id: 'chargebacks', label: 'Chargebacks', path: '/payment-operations/chargebacks', icon: 'AlertTriangle' },
        ],
      },
      { id: 'settlements', label: 'Settlements', path: '/payment-operations/settlements', icon: 'Banknote' },
      { id: 'payment-link-management', label: 'Payment Link Management', path: '/payment-link-management', icon: 'Link' },
    ],
  },
  {
    groupLabel: 'FINANCE',
    items: [
      {
        id: 'finance', label: 'Finance', icon: 'Landmark',
        children: [
          { id: 'chart-of-accounts', label: 'Chart of Accounts', path: '/finance/chart-of-accounts', icon: 'BookOpen' },
          { id: 'account-types', label: 'Account Types', path: '/finance/account-types', icon: 'Layers' },
          { id: 'balance-sheet', label: 'Balance Sheet', path: '/finance/balance-sheet', icon: 'Scale' },
          { id: 'income-statement', label: 'Income Statement', path: '/finance/income-statement', icon: 'TrendingUp' },
          { id: 'transaction-journal', label: 'Transaction Journal', path: '/finance/transaction-journal', icon: 'BookOpen' },
        ],
      },
    ],
  },
  {
    groupLabel: 'MERCHANTS',
    items: [
      {
        id: 'merchant-management', label: 'Merchant Management', icon: 'Store',
        children: [
          { id: 'merchant-list', label: 'Merchant List', path: '/merchants', icon: 'Building2' },
        ],
      },
    ],
  },
  {
    groupLabel: 'INTERNAL',
    items: [
      {
        id: 'user-management', label: 'User Management', icon: 'Users',
        children: [
          { id: 'users', label: 'Users', path: '/users', icon: 'UserCircle' },
          { id: 'user-roles', label: 'User Roles', path: '/rbac/user-roles', icon: 'Users' },
          { id: 'portfolio-management', label: 'Portfolio Management', path: '/portfolio-management', icon: 'Briefcase' },
        ],
      },
      {
        id: 'onboarding', label: 'Onboarding', icon: 'ClipboardList',
        children: [
          { id: 'onboarding-setup', label: 'Setup', path: '/onboarding/setup', icon: 'Settings' },
          { id: 'onboarding-approval', label: 'Approval', path: '/onboarding/approval', icon: 'CheckCircle' },
        ],
      },
      {
        id: 'access-control', label: 'Access Control', icon: 'Shield',
        children: [
          { id: 'roles', label: 'Roles', path: '/rbac/roles', icon: 'UserCog' },
          { id: 'permissions', label: 'Permissions', path: '/rbac/permissions', icon: 'Lock' },
          { id: 'permission-groups', label: 'Permission Groups', path: '/rbac/permission-groups', icon: 'FolderKey' },
          { id: 'role-permissions', label: 'Role Permissions', path: '/rbac/role-permissions', icon: 'ShieldCheck' },
        ],
      },
      { id: 'audit-logs', label: 'Audit Logs', path: '/audit-logs', icon: 'FileSearch' },
    ],
  },
  {
    groupLabel: 'INTEGRATION',
    items: [
      { id: 'api-keys', label: 'API Credentials', path: '/api-keys', icon: 'Key' },
      { id: 'api-documentation', label: 'API Documentation', path: '/integration/api-documentation', icon: 'Code2' },
    ],
  },
];

const DynamicSidebar = ({ isCollapsed = false, onToggle }) => {
  const location = useLocation();
  const { user, organization, role, logout, permissions } = useAuth();
  const [expandedSections, setExpandedSections] = useState([]);

  // Build a Set of accessible routes from permissions
  const accessibleRoutes = React.useMemo(() => {
    return new Set(permissions?.map(p => p?.route) || []);
  }, [permissions]);

  // Check if an item is accessible based on permissions
  const isItemAccessible = (item) => {
    if (item?.children?.length > 0) {
      return item?.children?.some(child => isItemAccessible(child));
    }
    return item?.path ? accessibleRoutes?.has(item?.path) : false;
  };

  // Filter children of an item to only accessible ones
  const filterItem = (item) => {
    if (item?.children?.length > 0) {
      const accessibleChildren = item?.children?.filter(child => isItemAccessible(child));
      if (accessibleChildren?.length === 0) return null;
      return { ...item, children: accessibleChildren };
    }
    return isItemAccessible(item) ? item : null;
  };

  // Build filtered menu groups
  const filteredMenuGroups = React.useMemo(() => {
    if (!permissions || permissions?.length === 0) return [];
    return menuGroups?.map(group => ({
        ...group,
        items: group?.items?.map(item => filterItem(item))?.filter(Boolean),
      }))?.filter(group => group?.items?.length > 0);
  }, [accessibleRoutes, permissions]);

  const toggleSection = (id) => {
    setExpandedSections(prev =>
      prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]
    );
  };

  const isActive = (path) => location?.pathname === path;

  const isSectionActive = (item) => {
    if (item?.path) return isActive(item?.path);
    if (item?.children) return item?.children?.some(c => isSectionActive(c));
    return false;
  };

  const renderItem = (item, level = 0) => {
    const active = isSectionActive(item);
    const expanded = expandedSections?.includes(item?.id);
    const hasChildren = item?.children?.length > 0;

    if (hasChildren) {
      return (
        <div key={item?.id}>
          <button
            onClick={() => toggleSection(item?.id)}
            title={isCollapsed ? item?.label : ''}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 group ${
              active
                ? 'bg-primary/10 text-primary' :'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-3">
              <Icon
                name={item?.icon}
                size={17}
                className={active ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600'}
              />
              {!isCollapsed && <span className="truncate">{item?.label}</span>}
            </div>
            {!isCollapsed && (
              <Icon
                name="ChevronDown"
                size={14}
                className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''} ${active ? 'text-primary' : 'text-gray-400'}`}
              />
            )}
          </button>
          {!isCollapsed && expanded && (
            <div className="mt-0.5 ml-4 pl-3 border-l border-gray-200 space-y-0.5 mb-1">
              {item?.children?.map(child => renderItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item?.id}
        to={item?.path}
        title={isCollapsed ? item?.label : ''}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 group ${
          isActive(item?.path)
            ? 'bg-primary/10 text-primary font-semibold' :'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        {isActive(item?.path) && (
          <span className="absolute left-0 w-0.5 h-7 bg-primary rounded-r-full" />
        )}
        <Icon
          name={item?.icon}
          size={17}
          className={isActive(item?.path) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600'}
        />
        {!isCollapsed && <span className="truncate">{item?.label}</span>}
      </Link>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white">
      {/* Logo / Brand */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img src="/assets/images/image-1782219559185.png" alt="App logo" className="w-full h-full object-cover" />
        </div>
        {!isCollapsed && (
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">DynamicPay</p>
            {organization?.name && (
              <p className="text-xs text-gray-400 truncate">{organization?.name}</p>
            )}
          </div>
        )}
        {!isCollapsed && (
          <button
            onClick={onToggle}
            className="ml-auto p-1 rounded hover:bg-gray-100 text-gray-400 flex-shrink-0"
          >
            <Icon name="ChevronLeft" size={16} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4 relative">
        {filteredMenuGroups?.map((group) => (
          <div key={group?.groupLabel}>
            {!isCollapsed && (
              <p className="px-3 mb-1 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
                {group?.groupLabel}
              </p>
            )}
            <div className="space-y-0.5 relative">
              {group?.items?.map(item => renderItem(item))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-100 px-2 py-3 space-y-0.5">
        {!isCollapsed ? (
          <>
            <Link
              to="/forgot-password"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              <Icon name="KeyRound" size={16} className="text-gray-400" />
              <span>Reset Password</span>
            </Link>
            <button
              onClick={() => logout()}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-50"
            >
              <Icon name="LogOut" size={16} />
              <span>Sign Out</span>
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
            <button
              onClick={() => logout()}
              className="p-2 rounded-lg text-red-500 hover:bg-red-50"
            >
              <Icon name="LogOut" size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] border-r border-gray-200 z-900 transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-60'
        } hidden lg:block overflow-hidden`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Overlay */}
      <div className={`fixed inset-0 z-900 lg:hidden ${isCollapsed ? 'pointer-events-none' : ''}`}>
        <div
          className={`absolute inset-0 bg-black transition-opacity ${isCollapsed ? 'opacity-0' : 'opacity-50'}`}
          onClick={onToggle}
        />
        <aside
          className={`absolute left-0 top-0 h-full w-64 border-r border-gray-200 transform transition-transform ${
            isCollapsed ? '-translate-x-full' : 'translate-x-0'
          } overflow-hidden`}
        >
          <div className="flex flex-col h-full bg-white">
            <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img src="/assets/images/image-1782219559185.png" alt="App logo" className="w-full h-full object-cover" />
              </div>
              <span className="font-bold text-gray-900">DynamicPay</span>
              <button onClick={onToggle} className="ml-auto p-1 rounded hover:bg-gray-100">
                <Icon name="X" size={18} className="text-gray-400" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4 relative">
              {filteredMenuGroups?.map((group) => (
                <div key={group?.groupLabel}>
                  <p className="px-3 mb-1 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
                    {group?.groupLabel}
                  </p>
                  <div className="space-y-0.5 relative">
                    {group?.items?.map(item => renderItem(item))}
                  </div>
                </div>
              ))}
            </nav>
            <div className="border-t border-gray-100 px-2 py-3 space-y-0.5">
              <Link
                to="/forgot-password"
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
              >
                <Icon name="KeyRound" size={16} className="text-gray-400" />
                <span>Reset Password</span>
              </Link>
              <button
                onClick={() => logout()}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-50"
              >
                <Icon name="LogOut" size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};

export default DynamicSidebar;
